import { VoiceTextarea } from '../components/VoiceTextarea';
// src/modules/FeedbackModule.tsx
import React, { useState, useEffect } from 'react';
import { User, ThumbsUp, ThumbsDown, MessageSquare, Calendar, Star, Plus, Trash2, Edit, Save, X } from 'lucide-react';
import { VoiceInput } from '../components/VoiceInput';

interface Feedback {
  id: string;
  residentName: string;
  familyName: string;
  date: string;
  rating: 1 | 2 | 3 | 4 | 5;
  comment: string;
  category: 'Care' | 'Food' | 'Activities' | 'Environment' | 'Staff' | 'Other';
  resolved: boolean;
  actionTaken: string | null;
}

export function FeedbackModule({ user }: { user: any }) {
  const [feedback, setFeedback] = useState<Feedback[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [newFeedback, setNewFeedback] = useState<Partial<Feedback>>({
    residentName: '',
    familyName: '',
    rating: 5,
    comment: '',
    category: 'Care',
    resolved: false,
    actionTaken: null,
  });
  const [message, setMessage] = useState('');

  useEffect(() => {
    const stored = localStorage.getItem('rmacs_feedback');
    if (stored) setFeedback(JSON.parse(stored));
    else localStorage.setItem('rmacs_feedback', JSON.stringify([]));
  }, []);

  const saveFeedback = (data: Feedback[]) => {
    localStorage.setItem('rmacs_feedback', JSON.stringify(data));
    setFeedback(data);
  };

  const addFeedback = () => {
    if (!newFeedback.residentName || !newFeedback.comment) {
      setMessage('Please fill in resident name and comment.');
      return;
    }
    const item: Feedback = {
      id: Date.now().toString(36) + Math.random().toString(36).substr(2, 5),
      residentName: newFeedback.residentName!,
      familyName: newFeedback.familyName || '',
      date: new Date().toISOString(),
      rating: newFeedback.rating!,
      comment: newFeedback.comment!,
      category: newFeedback.category!,
      resolved: false,
      actionTaken: null,
    };
    saveFeedback([item, ...feedback]);
    setShowForm(false);
    setNewFeedback({
      residentName: '',
      familyName: '',
      rating: 5,
      comment: '',
      category: 'Care',
      resolved: false,
      actionTaken: null,
    });
    setMessage('Feedback saved!');
    setTimeout(() => setMessage(''), 3000);
  };

  const resolveFeedback = (id: string, action: string) => {
    const updated = feedback.map(f => f.id === id ? { ...f, resolved: true, actionTaken: action } : f);
    saveFeedback(updated);
  };

  const deleteFeedback = (id: string) => {
    if (!window.confirm('Delete this feedback?')) return;
    const filtered = feedback.filter(f => f.id !== id);
    saveFeedback(filtered);
  };

  const getRatingStars = (rating: number) => {
    return '⭐'.repeat(rating) + '☆'.repeat(5 - rating);
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl border border-[#E1DCCD] p-6 flex flex-wrap justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-[#123B36] flex items-center gap-2">
            <MessageSquare className="w-6 h-6" /> Family & Resident Feedback
          </h2>
          <p className="text-[#6B7A76]">Capture, track, and respond to feedback from residents and families.</p>
        </div>
        <button onClick={() => setShowForm(true)} className="bg-[#123B36] text-white px-4 py-2 rounded-lg hover:bg-[#0E2C28] flex items-center gap-2">
          <Plus className="w-4 h-4" /> New Feedback
        </button>
      </div>

      {message && <div className="p-3 rounded-xl text-sm bg-green-50 text-green-800 border border-green-200">{message}</div>}

      {showForm && (
        <div className="bg-white rounded-2xl border border-[#E1DCCD] p-6">
          <h3 className="text-lg font-bold text-[#123B36] mb-4">Record Feedback</h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Resident Name</label>
              <input value={newFeedback.residentName || ''} onChange={(e) => setNewFeedback({ ...newFeedback, residentName: e.target.value })} className="w-full border rounded px-3 py-2" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Family Member (optional)</label>
              <input value={newFeedback.familyName || ''} onChange={(e) => setNewFeedback({ ...newFeedback, familyName: e.target.value })} className="w-full border rounded px-3 py-2" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Category</label>
              <select value={newFeedback.category || 'Care'} onChange={(e) => setNewFeedback({ ...newFeedback, category: e.target.value as any })} className="w-full border rounded px-3 py-2">
                <option value="Care">Care</option>
                <option value="Food">Food</option>
                <option value="Activities">Activities</option>
                <option value="Environment">Environment</option>
                <option value="Staff">Staff</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Rating (1-5)</label>
              <select value={newFeedback.rating || 5} onChange={(e) => setNewFeedback({ ...newFeedback, rating: parseInt(e.target.value) as 1|2|3|4|5 })} className="w-full border rounded px-3 py-2">
                <option value="1">1 - Poor</option>
                <option value="2">2 - Below Average</option>
                <option value="3">3 - Average</option>
                <option value="4">4 - Good</option>
                <option value="5">5 - Excellent</option>
              </select>
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700">Comment</label>
              <VoiceInput
                onTranscript={(text) => setNewFeedback({ ...newFeedback, comment: text })}
                className="mb-1"
                placeholder="Or dictate your comment..."
              />
              <VoiceTextarea
                value={newFeedback.comment || ''}
                onChange={(e) => setNewFeedback({ ...newFeedback, comment: e.target.value })}
                rows={3}
                className="w-full border rounded px-3 py-2 resize-none"
              />
            </div>
          </div>
          <div className="mt-4 flex gap-2">
            <button onClick={addFeedback} className="bg-[#123B36] text-white px-6 py-2 rounded-lg hover:bg-[#0E2C28]">Save</button>
            <button onClick={() => setShowForm(false)} className="bg-gray-200 px-6 py-2 rounded-lg hover:bg-gray-300">Cancel</button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-[#E1DCCD] overflow-hidden">
        <div className="bg-[#F7F5EE] px-6 py-3 border-b border-[#E1DCCD] flex justify-between items-center">
          <h3 className="font-bold text-[#123B36]">All Feedback</h3>
          <span className="text-sm text-gray-500">{feedback.filter(f => !f.resolved).length} unresolved</span>
        </div>
        <div className="divide-y divide-[#E1DCCD] max-h-96 overflow-y-auto">
          {feedback.length === 0 ? (
            <div className="p-6 text-center text-gray-400">No feedback recorded yet.</div>
          ) : (
            feedback.map(f => (
              <div key={f.id} className={`p-4 ${!f.resolved ? 'bg-yellow-50/30' : 'bg-white'}`}>
                <div className="flex justify-between items-start">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{f.residentName}</span>
                      <span className="text-xs text-gray-500">{f.category}</span>
                      <span className="text-xs">{getRatingStars(f.rating)}</span>
                    </div>
                    <p className="text-sm text-[#6B7A76] mt-1">{f.comment}</p>
                    {f.familyName && <p className="text-xs text-gray-400">From: {f.familyName}</p>}
                    <p className="text-xs text-gray-400">{new Date(f.date).toLocaleDateString()}</p>
                    {f.resolved && f.actionTaken && <p className="text-xs text-green-600 mt-1">✅ Resolved: {f.actionTaken}</p>}
                  </div>
                  <div className="flex gap-2">
                    {!f.resolved && (
                      <button onClick={() => {
                        const action = window.prompt('Enter action taken:');
                        if (action) resolveFeedback(f.id, action);
                      }} className="text-[#123B36] text-xs hover:underline">Resolve</button>
                    )}
                    <button onClick={() => deleteFeedback(f.id)} className="text-red-500 text-xs hover:underline">Delete</button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}